'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../lib/i18n';
import { useNationalEnvironment } from '../lib/useNationalEnvironment';



function dateSep(fmt: string): string {
  return fmt.includes('/') ? '/' : '.';
}

function isoToDisplay(iso: string, fmt: string): string {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const sep = dateSep(fmt);
  return `${parts[2]}${sep}${parts[1]}${sep}${parts[0]}`;
}

function displayToIso(display: string, fmt: string): string {
  if (!display) return '';
  const sep = dateSep(fmt);
  // Normalize: strip trailing separator (e.g. "25.7." → "25.7")
  const trimmed = display.trim().replace(new RegExp(`\\${sep}$`), '');
  const parts = trimmed.split(sep);
  const currentYear = new Date().getFullYear();
  let dd: string, mm: string, yyyy: string;
  if (parts.length === 2) {
    // Only day + month — auto-add current year
    [dd, mm] = parts;
    yyyy = String(currentYear);
  } else if (parts.length === 3) {
    [dd, mm, yyyy] = parts;
    if (yyyy.length === 2) {
      // 2-digit year — prepend century
      yyyy = String(currentYear).substring(0, 2) + yyyy;
    }
  } else {
    return '';
  }
  if (!dd || !mm || !yyyy) return '';
  if (yyyy.length < 4) return '';
  const ddNum = parseInt(dd);
  const mmNum = parseInt(mm);
  if (isNaN(ddNum) || isNaN(mmNum) || ddNum < 1 || ddNum > 31 || mmNum < 1 || mmNum > 12) return '';
  return `${yyyy}-${String(mmNum).padStart(2, '0')}-${String(ddNum).padStart(2, '0')}`;
}

function getCalendarDays(year: number, month: number): { day: number; currentMonth: boolean; iso: string }[] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days: { day: number; currentMonth: boolean; iso: string }[] = [];

  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ day: d.getDate(), currentMonth: false, iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` });
  }
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ day: i, currentMonth: true, iso: `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}` });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    const d = new Date(year, month + 1, i);
    days.push({ day: d.getDate(), currentMonth: false, iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` });
  }
  return days;
}

interface LocalizedDateInputProps {
  value: string;
  onChange: (value: string) => void;
  locale: string;
  disabled?: boolean;
  className?: string;
  min?: string;
  required?: boolean;
}

export function LocalizedDateInput({ value, onChange, locale, disabled, className, min, required }: LocalizedDateInputProps) {
  const { t } = useTranslation();
  const { env } = useNationalEnvironment();

  const MONTH_NAMES: Record<string, string[]> = {
  cs: ['Leden', t('únor'), t('březen'), 'Duben', t('květen'), t('červen'), t('červenec'), 'Srpen', t('září'), t('říjen'), 'Listopad', 'Prosinec'],
  sk: [t('január'), t('február'), 'Marec', t('apríl'), t('máj'), t('jún'), t('júl'), 'August', 'September', t('október'), 'November', 'December'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  uk: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'],
  };

  const WEEKDAY_SHORT: Record<string, string[]> = {
  cs: ['Po', t('út'), 'St', t('čt'), t('pá'), 'So', 'Ne'],
  sk: ['Po', 'Ut', 'St', t('št'), 'Pi', 'So', 'Ne'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
  };
  // Date format comes from the national environment (country), month/weekday
  // names stay language-driven via the `locale` prop.
  const fmt = env.dateFormat || 'dd.MM.yyyy';
  const monthNames = MONTH_NAMES[locale] || MONTH_NAMES['cs'];
  const weekdayShort = WEEKDAY_SHORT[locale] || WEEKDAY_SHORT['cs'];
  const [display, setDisplay] = useState(isoToDisplay(value, fmt));
  const [showPicker, setShowPicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => {
    if (value) {
      const parts = value.split('-');
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    setDisplay(isoToDisplay(value, fmt));
  }, [value, fmt]);

  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    const updatePos = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const popupW = 256;
        const popupH = 320;
        let top = rect.bottom + 4;
        let left = rect.right - popupW;
        if (left < 8) left = 8;
        if (top + popupH > window.innerHeight - 8) {
          top = rect.top - popupH - 4;
        }
        if (top < 8) top = 8;
        setPopupPos({ top, left });
      }
    };
    updatePos();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [showPicker]);

  const handleBlur = () => {
    const iso = displayToIso(display, fmt);
    if (iso !== value) {
      onChange(iso);
    }
    if (iso) {
      // Update display to show normalized date (with year filled in)
      const normalized = isoToDisplay(iso, fmt);
      if (normalized !== display) {
        setDisplay(normalized);
      }
    } else if (display) {
      setDisplay(isoToDisplay(value, fmt));
    }
  };

  const selectDate = (iso: string) => {
    onChange(iso);
    setDisplay(isoToDisplay(iso, fmt));
    setShowPicker(false);
  };

  const prevMonth = () => setPickerMonth(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() - 1, 1));
  const nextMonth = () => setPickerMonth(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() + 1, 1));

  const calendarDays = getCalendarDays(pickerMonth.getFullYear(), pickerMonth.getMonth());
  const todayIso = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={display}
          onChange={e => setDisplay(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleBlur();
            }
          }}
          disabled={disabled}
          required={required}
          placeholder={fmt}
          className={`${className || 'w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss'} flex-1`}
        />
        <button
          type="button"
          ref={buttonRef}
          onClick={() => !disabled && setShowPicker(!showPicker)}
          disabled={disabled}
          className="rounded-rustic border border-wheat bg-cream px-2 py-2 text-wood hover:bg-linen disabled:opacity-50"
          tabIndex={-1}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
      {showPicker && popupPos && (
        <div
          className="fixed z-[10000] rounded-rustic border border-wheat bg-cream shadow-xl p-3 w-64"
          style={{ top: popupPos.top, left: popupPos.left }}
        >
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevMonth} className="text-wood hover:text-bark px-2 py-1 rounded hover:bg-linen">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <span className="text-sm font-medium text-bark">{monthNames[pickerMonth.getMonth()]} {pickerMonth.getFullYear()}</span>
            <button type="button" onClick={nextMonth} className="text-wood hover:text-bark px-2 py-1 rounded hover:bg-linen">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekdayShort.map(d => (
              <div key={d} className="text-center text-xs text-wood font-medium py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((d, i) => {
              const isSelected = d.iso === value;
              const isToday = d.iso === todayIso;
              const isBeforeMin = min ? d.iso < min : false;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !isBeforeMin && selectDate(d.iso)}
                  disabled={isBeforeMin}
                  className={`text-xs rounded py-1.5 transition ${
                    isSelected
                      ? 'bg-moss text-cream font-medium'
                      : isBeforeMin
                      ? 'text-wood/30 cursor-not-allowed'
                      : isToday
                      ? 'bg-moss/20 text-moss-dark font-bold ring-1 ring-moss hover:bg-moss/30'
                      : d.currentMonth
                      ? 'text-bark hover:bg-linen'
                      : 'text-wood/40 hover:bg-linen'
                  }`}
                >
                  {d.day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

interface LocalizedTimeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const TIME_SLOTS: string[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

export function LocalizedTimeInput({ value, onChange, disabled, className }: LocalizedTimeInputProps) {
  const [showSlots, setShowSlots] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!showSlots) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSlots(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSlots]);

  const normalizeTime = (raw: string): string => {
    const cleaned = raw.trim().replace(/[.,]/g, ':');
    const parts = cleaned.split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0]);
      const m = parseInt(parts[1]);
      if (!isNaN(h) && !isNaN(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }
    return '';
  };

  const handleBlur = () => {
    const normalized = normalizeTime(inputValue);
    if (normalized) {
      setInputValue(normalized);
      if (normalized !== value) onChange(normalized);
    } else if (inputValue) {
      setInputValue(value);
    }
  };

  const selectSlot = (slot: string) => {
    setInputValue(slot);
    onChange(slot);
    setShowSlots(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder="HH:MM"
          className={`${className || 'w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss'} flex-1`}
        />
        <button
          type="button"
          onClick={() => !disabled && setShowSlots(!showSlots)}
          disabled={disabled}
          className="rounded-rustic border border-wheat bg-cream px-2 py-2 text-wood hover:bg-linen disabled:opacity-50"
          tabIndex={-1}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>
      {showSlots && (
        <div className="absolute z-50 mt-1 rounded-rustic border border-wheat bg-cream shadow-lg p-2 w-48 max-h-60 overflow-y-auto">
          <div className="grid grid-cols-4 gap-1">
            {TIME_SLOTS.map(t => (
              <button
                key={t}
                type="button"
                onClick={() => selectSlot(t)}
                className={`text-xs rounded py-1 transition ${t === value ? 'bg-moss text-cream font-medium' : 'text-bark hover:bg-linen'}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
